
import React, { useState } from 'react';
import { NodeType, AgentType } from '../types';
import { Activity, HelpCircle, Hammer, Cpu, ChevronLeft, ChevronRight, Menu } from 'lucide-react';
import BuildPanel from './BuildPanel';
import SimulatePanel from './SimulatePanel';
import { useAppContext } from '../contexts/AppContext';

interface ControlsProps {
  onSpawnAgent: (type: AgentType) => void;
  onTriggerMCI: (mciType: 'EPIDEMIC' | 'ACCIDENT' | 'BIOHAZARD') => void;
  onUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onExport: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
}

const Controls: React.FC<ControlsProps> = ({ 
  onSpawnAgent, 
  onTriggerMCI, 
  onUploadImage,
  onSave,
  onLoad,
  onExport,
  onCopy,
  onPaste,
  onDuplicate
}) => {
  const [activeTab, setActiveTab] = useState<'BUILD' | 'SIMULATE'>('BUILD');
  const { state, dispatch } = useAppContext();
  const { isControlsVisible } = state;

  const openHelp = (tab: 'BASICS' | 'BUILD' | 'SIM' | 'LEGEND' = 'BASICS') => {
    dispatch({ type: 'SET_HELP_OPEN', payload: { open: true, tab } });
  };

  const toggleControls = () => {
    dispatch({ type: 'TOGGLE_CONTROLS' });
  };

  return (
    <div className={`fixed inset-y-0 left-0 z-50 flex max-w-full items-center pointer-events-none transition-transform duration-150 ease-out motion-reduce:transition-none ${isControlsVisible ? 'translate-x-0' : '-translate-x-[min(384px,calc(100vw-40px))]'}`}>
      
      {/* Main Panel Container */}
      <div className="ml-3 h-[calc(100vh-24px)] w-[min(384px,calc(100vw-52px))] pointer-events-auto bg-slate-900 border border-slate-700/80 rounded-xl shadow-xl flex flex-col overflow-hidden relative">
        
        <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-800 px-4 py-3">
          <h1 className="flex items-center gap-2 font-rajdhani text-lg font-bold text-white">
            <Activity className="text-cyan-400" size={20} /> SMPS PERKĖLIMAS
          </h1>
          <button 
            onClick={() => openHelp(activeTab === 'BUILD' ? 'BUILD' : 'SIM')} 
            className="flex min-h-9 items-center gap-1.5 rounded-md border border-slate-700 bg-slate-950/50 px-2.5 py-1.5 text-xs font-semibold text-slate-300 transition-colors duration-150 hover:border-cyan-700 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 motion-reduce:transition-none"
          >
            <HelpCircle size={12} /> INSTRUKCIJA
          </button>
        </div>

        <div className="flex bg-slate-950/80 flex-shrink-0">
          <button onClick={() => setActiveTab('BUILD')} className={`relative flex flex-1 items-center justify-center gap-2 py-3 font-rajdhani text-xs font-bold uppercase tracking-wide transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-400 ${activeTab === 'BUILD' ? 'bg-cyan-950/40 text-cyan-300' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`}>
              {activeTab === 'BUILD' && <div className="absolute inset-x-0 bottom-0 h-0.5 bg-cyan-500"></div>}
              <Hammer size={14} /> Architektas
          </button>
          <button onClick={() => setActiveTab('SIMULATE')} className={`relative flex flex-1 items-center justify-center gap-2 py-3 font-rajdhani text-xs font-bold uppercase tracking-wide transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-400 ${activeTab === 'SIMULATE' ? 'bg-emerald-950/40 text-emerald-300' : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}`}>
              {activeTab === 'SIMULATE' && <div className="absolute inset-x-0 bottom-0 h-0.5 bg-emerald-500"></div>}
              <Cpu size={14} /> Simuliacija
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
            {activeTab === 'BUILD' ? (
                <BuildPanel 
                  onUploadImage={onUploadImage} 
                  onSave={onSave}
                  onLoad={onLoad}
                  onExport={onExport}
                  onCopy={onCopy}
                  onPaste={onPaste}
                  onDuplicate={onDuplicate}
                />
            ) : (
                <SimulatePanel onSpawnAgent={onSpawnAgent} onTriggerMCI={onTriggerMCI} />
            )}
        </div>
      </div>

      {/* Persistent Toggle Tab - Sticks out from the panel */}
      <div className="h-full flex items-center pointer-events-auto">
        <button 
          onClick={toggleControls}
          className={`
            group flex h-24 w-10 items-center justify-center rounded-r-xl border-y border-r border-slate-700 bg-slate-900 shadow-lg transition-colors duration-150 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 motion-reduce:transition-none
          `}
          title={isControlsVisible ? "Slėpti valdymo juostą" : "Rodyti valdymo juostą"}
        >
          <div className="flex flex-col items-center gap-1">
            {isControlsVisible ? (
              <ChevronLeft size={24} className="text-cyan-400 group-hover:scale-125 transition-transform" />
            ) : (
              <>
                <Menu size={20} className="text-cyan-400" />
                <div className="[writing-mode:vertical-lr] text-xs font-semibold text-cyan-300 uppercase tracking-wide">
                  MENIU
                </div>
              </>
            )}
          </div>
        </button>
      </div>
    </div>
  );
};

export default Controls;
